// src/features/map/layers/WeatherChip.jsx
// Map-corner weather+AQI chip (Task 5). Renders null until availability +
// a successful fetch — a flaky/misconfigured OpenWeatherMap key must never
// surface as a broken widget. Refetches on mount, on map moveend (debounced
// so a drag doesn't spam the point endpoint), and on a 10-min interval.
// Click toggles the WeatherPanel drawer, which fetches its own richer
// (forecast-included) payload — the chip's cached point stays current-only.
import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { layers as layersApi } from '@/lib/trackizy';
import { iconEmoji, aqiSeverity } from './weatherFormat';
import WeatherPanel from './WeatherPanel';

const MOVEEND_DEBOUNCE_MS = 1500;
const REFRESH_INTERVAL_MS = 10 * 60 * 1000;

export default function WeatherChip({ mapRef, available }) {
  const { i18n } = useTranslation();
  const [point, setPoint] = useState(null); // { current, aqi }
  const [center, setCenter] = useState(null); // last-fetched {lat, lon}, fed to the panel
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  // Request-generation guard: a slow response for center A must not overwrite
  // a newer response for center B (a warm server cache can resolve B instantly
  // while cold A is still in flight). Only the latest request applies state;
  // the effect cleanup also bumps the generation so responses from a torn-down
  // effect (available flip / unmount) are ignored.
  const requestIdRef = useRef(0);

  const fetchAt = useCallback((lat, lon) => {
    const id = ++requestIdRef.current;
    layersApi.weatherPoint(lat, lon, { lang: i18n.language })
      .then((res) => {
        if (id !== requestIdRef.current) return;
        if (!res?.current) { setPoint(null); return; }
        setPoint(res);
        setCenter({ lat, lon });
      })
      .catch(() => {
        if (id === requestIdRef.current) setPoint(null);
      });
  }, [i18n.language]);

  useEffect(() => {
    if (!available) { setPoint(null); return undefined; }
    // Tolerates both ref shapes: MapView's internal mapRef (raw map) and a
    // parent's mapViewRef (imperative handle exposing getMap()).
    const resolveMap = () => mapRef?.current?.getMap?.() || mapRef?.current;
    const map = resolveMap();
    const fetchFromCenter = () => {
      const c = resolveMap()?.getCenter?.();
      if (!c) return;
      // MapLibre's getCenter().lng is unwrapped and can drift arbitrarily far
      // past ±180 after repeated panning around the antimeridian — wrap it
      // back into range before it hits the point endpoint's ±180 validation.
      const lon = ((c.lng + 180) % 360 + 360) % 360 - 180;
      fetchAt(c.lat, lon);
    };
    fetchFromCenter();
    const interval = setInterval(fetchFromCenter, REFRESH_INTERVAL_MS);

    const onMoveEnd = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(fetchFromCenter, MOVEEND_DEBOUNCE_MS);
    };
    map?.on?.('moveend', onMoveEnd);

    return () => {
      requestIdRef.current += 1; // invalidate in-flight responses from this effect
      clearInterval(interval);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      map?.off?.('moveend', onMoveEnd);
    };
  }, [available, mapRef, fetchAt]);

  // tempC null (e.g. the current-conditions block failed upstream while AQI
  // succeeded) would otherwise render "0°" via Math.round(null) — treat it
  // the same as a failed fetch rather than showing a misleading reading.
  if (!available || !point?.current || point.current.tempC == null) return null;
  const { current, aqi } = point;
  const severity = aqi ? aqiSeverity(aqi.index) : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={current.desc}
        className="h-9 px-3 text-xs font-semibold rounded-xl flex items-center gap-1.5 transition-all shadow-md bg-white/90 text-slate-600 hover:bg-white border border-slate-200/80 dark:bg-slate-800/90 dark:text-slate-300 dark:border-slate-700/80 dark:hover:bg-slate-800"
        style={{ backdropFilter: 'blur(12px)' }}
      >
        <bdi>{Math.round(current.tempC)}°</bdi>
        <span>{iconEmoji(current.icon)}</span>
        {severity && (
          <span
            className="w-2.5 h-2.5 rounded-full shrink-0"
            style={{ background: severity.color }}
          />
        )}
      </button>
      {open && center && (
        <WeatherPanel
          point={{ lat: center.lat, lon: center.lon }}
          lang={i18n.language}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
